import { BreadcrumbItem, BreadcrumbLink } from "@chakra-ui/breadcrumb";
import { HStack, Stack, VStack } from "@chakra-ui/layout";
import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useToast } from "../../hooks/useToast";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { Card } from "../../components/Card/Card";
import { PageContent } from "../../components/PageContent/PageContent";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { useBusinessContext } from "../../context/ModelContext";
import { EditIcon, ZapIcon } from "../../styles/icons";
import { useApiClient } from "../../hooks/useApiClient";
import { EventForm } from "../../forms/Events/EventForm";
import { CherryPayApi } from "../../api/models";
import { useParams } from "react-router";
import { EventDetailCard } from "../../components/EventDetailCard/EventDetailCard";
import { Spinner } from "@chakra-ui/spinner";

interface RegistrationFormConfigurationDetailProps {
  create?: boolean;
}

export const EventDetail = (
  props: RegistrationFormConfigurationDetailProps
) => {
  const [searchParams] = useSearchParams();
  const { eventId: eventIdParam } = useParams();
  const business = useBusinessContext();
  const [event, setEvent] = useState<CherryPayApi.Event | null>(
    props.create ? null : null
  );
  const [isLoadingEvent, setIsLoadingEvent] = useState<boolean>(false);

  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useToast();

  const [editMode, setEditMode] = useState<boolean>(
    props.create ?? searchParams.get("edit") == "true"
  );

  const businessId = business.BusinessId;

  /**
   * In the create mode, we always show the form.
   * In edit mode, we show the form only after the event is loaded.
   * False otherwise.
   */
  const canShowForm = useMemo(() => {
    if (props.create) {
      return true;
    }

    if (editMode) {
      return event !== null && !isLoadingEvent;
    }

    return false;
  }, [props.create, editMode, event, isLoadingEvent]);

  const canShowDetail = useMemo(
    () => !editMode && event !== null,
    [editMode, event]
  );

  useEffect(() => {
    if (business.BusinessId && eventIdParam) {
      setIsLoadingEvent(true);
      apiClient.getEvent(businessId, eventIdParam).then((event) => {
        if (event.ok) {
          setEvent(event.data);
        } else {
          showErrorToast("Unable to load event. Please try again.");
        }
        setIsLoadingEvent(false);
      });
    }
  }, [eventIdParam]);

  const eventId = event?.id ?? "";

  const onSuccess = useCallback(
    (event: CherryPayApi.Event) => {
      setEditMode(false);
      setEvent(event);

      if (props.create) {
        showSuccessToast("Event created.");
        navigate(`/businesses/${businessId}/events/${event.Meta?.Id}`);
      } else {
        showSuccessToast("Event updated.");
      }
    },
    [showSuccessToast, event]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Event update failed.");
    },
    [showErrorToast]
  );

  return (
    <>
      <PageHeading>
        <BusinessBreadcrumbs>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to={`/businesses/${businessId}/events`}>
              Events
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink
              as={Link}
              to={`/businesses/${businessId}/events/${
                props.create ? "new" : eventId
              }`}
            >
              {props.create ? "New" : event?.EventName}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BusinessBreadcrumbs>
        <PageHeading.Title>
          {props.create ? "Create" : editMode ? "Edit" : "View"} Event
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <Link
          to={`/businesses/${businessId}/events`}
          style={{ alignSelf: "flex-start", marginBottom: "10px" }}
        >
          &lt; Back to All Events
        </Link>
        {isLoadingEvent && (
          <Card w="100%" p={8} textAlign="center">
            <Spinner />
          </Card>
        )}
        {canShowForm && (
          <EventForm
            apiClient={apiClient}
            businessId={businessId!}
            event={event}
            eventName={event?.EventName ?? "New Event"}
            onCancel={() =>
              props.create
                ? navigate(`/businesses/${businessId}/events`)
                : setEditMode(false)
            }
            onSuccess={onSuccess}
            onFailure={onFailure}
          />
        )}
        {canShowDetail && (
          <Card w="100%">
            <Stack
              w="100%"
              alignItems="start"
              spacing={{ base: 4, xl: 24 }}
              bgColor="gray.600"
              py={5}
              px={8}
              borderTopStartRadius="3xl"
              borderTopEndRadius="3xl"
              direction={{ base: "column", xl: "row" }}
            >
              <VStack spacing="1" alignItems="flex-start">
                <Text fontSize="2xl" color="white" fontWeight="bold">
                  {event?.EventName}
                </Text>
              </VStack>
              <HStack
                flex="1"
                justifyContent="end"
                position="relative"
                zIndex={100}
                alignSelf="center"
              >
                <Menu>
                  <MenuButton
                    leftIcon={<ZapIcon />}
                    colorScheme="cherryButton"
                    as={Button}
                    aria-label="Event Actions"
                  >
                    Event Actions
                  </MenuButton>
                  <MenuList>
                    {event && (
                      <>
                        <MenuItem
                          onClick={() => setEditMode(true)}
                          icon={<EditIcon />}
                        >
                          Edit Event
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Menu>
              </HStack>
            </Stack>

            <Tabs variant="edit" w="100%" isLazy>
              <TabList>
                <HStack spacing={3} overflowX="auto">
                  <Tab>Details</Tab>
                </HStack>
              </TabList>

              <TabPanels>
                <TabPanel>
                  {event && <EventDetailCard event={event} />}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Card>
        )}
      </PageContent>
    </>
  );
};
